<template>
  <div class="timer-toggle" :class="{'time-editing': isEditMode, 'active-stopwatch': isActiveCurrentStopwatch}">
    <div v-if="!isLoaded">
      <button class="btn btn-info px-2" style="min-width: 100px"><i class="fa fa-cog fa-spin"></i></button>
    </div>
    <div
      v-if="isLoaded"
    >
      <div class="flex flex-row btn-group">
        <button
          @click.right.prevent="toggleEditMode"
          :class="{
            'btn-danger': !isRunning && isActiveCurrentStopwatch,
            'btn-success': isRunning && isActiveCurrentStopwatch,
            'btn-info': !isActiveCurrentStopwatch,
            'is-mobile': isMobile
          }"
          class="btn btn-sm accept-log d-flex align-items-center justify-content-between position-relative btn-timer"
          type="button"
          @click="toggleTime"
        >
          <span :class="{'mx-auto': isMobile}" class="time-text">{{ currentTimeToShow }}</span>
          <span v-if="!isMobile" class="hot-key-label" style="font-size: 10px; width: 40px; position: static; transform: none;">Space</span>
        </button>
        <button
          class="btn btn-sm d-flex align-items-center justify-content-between position-relative btn-edit"
          :class="{
            'btn-success': isEditMode,
            'btn-info': !isEditMode,
            'is-mobile': isMobile
          }"
          @click="openEditModal()"
        >
          <i class="fa fa-pencil ml-1 pencil-icon"></i>
          <span v-if="!isMobile" class="hot-key-label ml-2" style="position: static; transform: none;">.</span>
        </button>
      </div>
    </div>
    <modal name="small-stopwatch-edit-modal" :adaptive="true" height="auto" :width="300" @opened="onOpened" @closed="onClosed">
      <div class="p-3 text-center">
        <h4>{{ $t('editTime') }}</h4>
        <div class="d-flex justify-content-center align-items-center mb-3">
          <!-- Minutes -->
          <div v-for="idx in [0, 1]" :key="'min-' + idx" class="d-flex flex-column align-items-center">
            <button class="btn btn-link p-0 mb-1 arrow-btn" @click="incrementDigit(idx)" @keydown.enter.prevent.stop="saveTime()">
              <i class="fa fa-chevron-up"></i>
            </button>
            <input
              :ref="`input${idx}`"
              v-model="timeInputs[idx]"
              type="text"
              pattern="\d*"
              inputmode="numeric"
              class="text-center mx-1 time-input"
              :class="{'has-error': inputErrors[idx]}"
              @input="onInput(idx)"
              @keydown.backspace="onBackspace(idx)"
              @keydown.enter="saveTime()"
              @keydown.esc="closeEditModal()"
              @keydown.up.prevent="incrementDigit(idx)"
              @keydown.down.prevent="decrementDigit(idx)"
              @keydown.left.prevent="onArrowLeft(idx)"
              @keydown.right.prevent="onArrowRight(idx)"
            >
            <button class="btn btn-link p-0 mt-1 arrow-btn" @click="decrementDigit(idx)" @keydown.enter.prevent.stop="saveTime()">
              <i class="fa fa-chevron-down"></i>
            </button>
          </div>

          <span class="h3 mb-0 mx-1">:</span>

          <!-- Seconds -->
          <div v-for="idx in [2, 3]" :key="'sec-' + idx" class="d-flex flex-column align-items-center">
            <button class="btn btn-link p-0 mb-1 arrow-btn" @click="incrementDigit(idx)" @keydown.enter.prevent.stop="saveTime()">
              <i class="fa fa-chevron-up"></i>
            </button>
            <input
              :ref="`input${idx}`"
              v-model="timeInputs[idx]"
              type="text"
              pattern="\d*"
              inputmode="numeric"
              class="text-center mx-1 time-input"
              :class="{'has-error': inputErrors[idx]}"
              @input="onInput(idx)"
              @keydown.backspace="onBackspace(idx)"
              @keydown.enter="saveTime()"
              @keydown.esc="closeEditModal()"
              @keydown.up.prevent="incrementDigit(idx)"
              @keydown.down.prevent="decrementDigit(idx)"
              @keydown.left.prevent="onArrowLeft(idx)"
              @keydown.right.prevent="onArrowRight(idx)"
            >
            <button class="btn btn-link p-0 mt-1 arrow-btn" @click="decrementDigit(idx)" @keydown.enter.prevent.stop="saveTime()">
              <i class="fa fa-chevron-down"></i>
            </button>
          </div>
        </div>
        <div class="d-flex justify-content-center">
          <button class="btn btn-primary" @click="saveTime()">{{ $t('save') }}</button>
        </div>
      </div>
    </modal>
  </div>

</template>
<script>
import { useStopwatch } from '@/use/useStopwatch'
import { useLive } from '@/views/live/use/useLive'
import { onMounted, onUnmounted, ref, nextTick, getCurrentInstance, computed } from 'vue'

export default {
  name: 'LeStopwatch',
  setup () {
    const { proxy } = getCurrentInstance()
    const isMobile = computed(() => proxy.$store.state.main.isMobile)
    const inputErrors = ref([false, false, false, false])
    const input0 = ref(null)
    const input1 = ref(null)
    const input2 = ref(null)
    const input3 = ref(null)
    const inputs = [input0, input1, input2, input3]

    const {
      focusSubstitutionModal,
      resetLog,
      keyboardShortcuts
    } = useLive()

    const {
      updateTime,
      config,
      gameUuid,
      currentClient,
      updateStopwatchTimeEverySecond,
      decreaseShotClockTime,
      increaseShotClockTime,
      currentShotClockToShow,
      currentTimeToShow,
      currentTime,
      currentShotClock,
      resetShotClock,
      toggleEditMode,
      increaseTime,
      decreaseTime,
      startTimer,
      stopTimer,
      initStopwatch,
      destroyStopwatch,
      isEditMode,
      isRunning,
      playBuzzer,
      isLoaded,
      activeStopwatch,
      toggleTime,
      ejectTimer,
      isActiveCurrentStopwatch,
      diffTime,
      prepareTimeInputs,
      timeInputs,
      updateStopwatchTime,
      periodMinTimeOnPeriod,
      periodMaxTimeOnPeriod,
      isReverse
    } = useStopwatch()

    function openEditModal () {
      if (!isActiveCurrentStopwatch.value) {
        return
      }
      resetLog()
      keyboardShortcuts.value = false
      stopTimer()
      prepareTimeInputs()
      inputErrors.value = [false, false, false, false]
      proxy.$modal.show('small-stopwatch-edit-modal')
    }

    function onOpened () {
      nextTick(() => {
        const input = inputs[0].value
        if (Array.isArray(input)) {
          input[0]?.focus()
          input[0]?.select()
        } else {
          input?.focus()
          input?.select()
        }
      })
    }

    function onClosed () {
      keyboardShortcuts.value = true
      focusSubstitutionModal()
    }

    async function onInput (index) {
      let val = timeInputs.value[index]
      if (val === undefined || val === null || val === '') {
        return
      }

      val = val.toString().replace(/\D/g, '')
      if (val === '') {
        timeInputs.value.splice(index, 1, '')
        return
      }

      if (val.length > 1) {
        val = val.slice(-1)
      }
      timeInputs.value.splice(index, 1, val)

      if (!isValidInput(index, val)) {
        timeInputs.value.splice(index, 1, '')
        setError(index)
        return
      }

      inputErrors.value[index] = false

      if (index < 3) {
        nextTick(() => {
          const nextInput = inputs[index + 1].value
          if (Array.isArray(nextInput)) {
            nextInput[0]?.focus()
            nextInput[0]?.select()
          } else {
            nextInput?.focus()
            nextInput?.select()
          }
        })
      } else if (index === 3) {
        await saveTime()
      }
    }

    const setError = (idx) => {
      inputErrors.value[idx] = true
      setTimeout(() => {
        inputErrors.value[idx] = false
      }, 800)
    }

    function isValidInput (index, val) {
      if (val === '') return true
      const numVal = parseInt(val)

      if (index === 2 && numVal > 5) {
        return false
      }

      // Dinamička validacija na osnovu granica perioda
      if (!isReverse.value) {
        const minMins = Math.floor(periodMinTimeOnPeriod.value / 1000 / 60)
        const maxMins = Math.floor(periodMaxTimeOnPeriod.value / 1000 / 60)
        const maxMinsStr = maxMins.toString().padStart(2, '0')

        if (index === 0) {
          // Dozvoljavamo unos prve cifre ako postoji bilo koji minut u opsegu koji počinje tom cifrom
          // npr. ako je opseg 00-10, za index 0 su validni '0' i '1'
          if (val < maxMinsStr[0] || val > maxMinsStr[0]) {
            // Provera da li postoji ijedna kombinacija [val, X] koja je unutar [minMins, maxMins]
            let possible = false
            for (let i = 0; i <= 9; i++) {
              const combined = parseInt(val + i.toString())
              if (combined >= minMins && combined <= maxMins) {
                possible = true
                break
              }
            }
            if (!possible) return false
          }
        }
        if (index === 1) {
          const currentMins = parseInt((timeInputs.value[0] || '0') + val)
          if (currentMins < minMins || currentMins > maxMins) {
            return false
          }
        }

        // Dodatna validacija za sekunde (index 2 i 3) kada su minuti na maksimumu
        if (index === 2 || index === 3) {
          const curMins = parseInt((timeInputs.value[0] || '0') + (timeInputs.value[1] || '0'))
          if (curMins === maxMins) {
            const maxSecs = Math.floor((periodMaxTimeOnPeriod.value / 1000) % 60)
            const maxSecsStr = maxSecs.toString().padStart(2, '0')
            if (index === 2 && val > maxSecsStr[0]) {
              return false
            }
            if (index === 3) {
              const curSecs = parseInt((timeInputs.value[2] || '0') + val)
              if (curSecs > maxSecs) {
                return false
              }
            }
          }
          // I za minimum
          if (curMins === minMins) {
            const minSecs = Math.floor((periodMinTimeOnPeriod.value / 1000) % 60)
            const minSecsStr = minSecs.toString().padStart(2, '0')
            if (index === 2 && val < minSecsStr[0]) {
              return false
            }
            if (index === 3) {
              const curSecs = parseInt((timeInputs.value[2] || '0') + val)
              if (curSecs < minSecs) {
                return false
              }
            }
          }
        }
      } else {
        // Reverse mod (vreme ide unazad od max ka 0)
        const maxMins = Math.floor(periodMaxTimeOnPeriod.value / 1000 / 60)
        const maxMinsStr = maxMins.toString().padStart(2, '0')

        if (index === 0 && val > maxMinsStr[0]) {
          return false
        }
        if (index === 1) {
          const currentMins = parseInt((timeInputs.value[0] || '0') + val)
          if (currentMins > maxMins) {
            return false
          }
        }

        if (index === 2 || index === 3) {
          const curMins = parseInt((timeInputs.value[0] || '0') + (timeInputs.value[1] || '0'))
          if (curMins === maxMins) {
            const maxSecs = Math.floor((periodMaxTimeOnPeriod.value / 1000) % 60)
            const maxSecsStr = maxSecs.toString().padStart(2, '0')
            if (index === 2 && val > maxSecsStr[0]) {
              return false
            }
            if (index === 3) {
              const curSecs = parseInt((timeInputs.value[2] || '0') + val)
              if (curSecs > maxSecs) {
                return false
              }
            }
          }
        }
      }

      return true
    }

    function incrementDigit (index) {
      const currentVal = parseInt(timeInputs.value[index] || '0')
      let newVal = (currentVal + 1) % 10

      if (!isValidInput(index, newVal.toString())) {
        // Ako novi broj nije validan (npr. prešli smo 5 kod desetica sekundi ili granicu perioda)
        // Pokušavamo da "vratimo" na najmanju moguću validnu cifru (obično 0)
        newVal = 0
        while (newVal < 10 && !isValidInput(index, newVal.toString())) {
          newVal++
        }
        // Ako smo prošli sve i ništa ne valja (teoretski ne bi trebalo), ne radimo ništa
        if (newVal === 10) {
          setError(index)
          return
        }
      }
      inputErrors.value[index] = false
      timeInputs.value.splice(index, 1, newVal.toString())
    }

    function decrementDigit (index) {
      const currentVal = parseInt(timeInputs.value[index] || '0')
      let newVal = (currentVal - 1 + 10) % 10

      if (!isValidInput(index, newVal.toString())) {
        // Ako novi broj nije validan (npr. skočili smo sa 0 na 9 a limit je 5)
        // Tražimo najveću moguću validnu cifru (obično 9, ili 5, ili niže zbog perioda)
        newVal = 9
        while (newVal >= 0 && !isValidInput(index, newVal.toString())) {
          newVal--
        }
        if (newVal < 0) {
          setError(index)
          return
        }
      }
      inputErrors.value[index] = false
      timeInputs.value.splice(index, 1, newVal.toString())
    }

    function closeEditModal () {
      proxy.$modal.hide('small-stopwatch-edit-modal')
    }

    function onBackspace (index) {
      if (timeInputs.value[index] === '' && index > 0) {
        const prevInput = inputs[index - 1].value
        if (Array.isArray(prevInput)) {
          prevInput[0]?.focus()
          prevInput[0]?.select()
        } else {
          prevInput?.focus()
          prevInput?.select()
        }
      }
    }

    function onArrowLeft (index) {
      if (index > 0) {
        const prevInput = inputs[index - 1].value
        if (Array.isArray(prevInput)) {
          prevInput[0]?.focus()
          prevInput[0]?.select()
        } else {
          prevInput?.focus()
          prevInput?.select()
        }
      }
    }

    function onArrowRight (index) {
      if (index < 3) {
        const nextInput = inputs[index + 1].value
        if (Array.isArray(nextInput)) {
          nextInput[0]?.focus()
          nextInput[0]?.select()
        } else {
          nextInput?.focus()
          nextInput?.select()
        }
      }
    }

    async function saveTime () {
      const mins = parseInt((timeInputs.value[0] || '0') + (timeInputs.value[1] || '0')) || 0
      const secs = parseInt((timeInputs.value[2] || '0') + (timeInputs.value[3] || '0')) || 0
      let totalMs = (mins * 60 + secs) * 1000

      // Validacija granica
      if (totalMs > periodMaxTimeOnPeriod.value) {
        totalMs = periodMaxTimeOnPeriod.value
      } else if (totalMs < periodMinTimeOnPeriod.value) {
        totalMs = periodMinTimeOnPeriod.value
      }

      if (isRunning.value) {
        await stopTimer()
      }

      diffTime.value = totalMs

      try {
        await updateStopwatchTime()
      } catch (e) {
        console.error(e)
      }

      proxy.$modal.hide('small-stopwatch-edit-modal')
    }

    function keyboardListener (event) {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return
      }
      if (['Space', 'Numpad0', 'Digit0'].includes(event.code)) {
        event.preventDefault()
        toggleTime()
      }
      if (['.', 'Decimal'].includes(event.key) || event.code === 'NumpadDecimal') {
        event.preventDefault()
        if (isActiveCurrentStopwatch.value) {
          openEditModal()
        }
      }
    }

    onMounted(() => {
      window.addEventListener('keydown', keyboardListener)
    })

    onUnmounted(() => {
      window.removeEventListener('keydown', keyboardListener)
    })

    return {
      updateTime,
      config,
      gameUuid,
      currentClient,
      updateStopwatchTimeEverySecond,
      decreaseShotClockTime,
      increaseShotClockTime,
      currentShotClockToShow,
      currentTimeToShow,
      resetShotClock,
      toggleEditMode,
      increaseTime,
      decreaseTime,
      startTimer,
      stopTimer,
      initStopwatch,
      destroyStopwatch,
      isEditMode,
      isRunning,
      playBuzzer,
      currentTime,
      isLoaded,
      activeStopwatch,
      toggleTime,
      ejectTimer,
      isActiveCurrentStopwatch,
      openEditModal,
      isMobile,
      onOpened,
      onClosed,
      timeInputs,
      inputErrors,
      onInput,
      onBackspace,
      onArrowLeft,
      onArrowRight,
      incrementDigit,
      decrementDigit,
      closeEditModal,
      saveTime,
      input0,
      input1,
      input2,
      input3,
      currentShotClock,
      diffTime,
      updateStopwatchTime,
      isReverse
    }
  }
}
</script>
<style scoped>
.time-input {
  outline: none;
  margin: 3px;
  padding: 5px;
  width: 40px;
  text-align: center;
  font-size: 1.1rem;
  border: none;
  border-radius: 3px;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
  caret-color: transparent;
}

.time-input:focus {
  box-shadow: 0 0 6px rgba(0, 123, 255, 0.8);
}

.time-input.has-error {
  box-shadow: 0 0 6px rgba(220, 53, 69, 0.8);
  border: 1px solid rgba(220, 53, 69, 0.5);
}

.arrow-btn {
  color: #888;
  font-size: 0.8rem;
  line-height: 1;
  transition: color 0.15s;
}

.arrow-btn:hover {
  color: #007bff;
  text-decoration: none;
}

.arrow-btn:focus {
  outline: none;
  box-shadow: none;
}

/* Hide arrows in number input */
.time-input::-webkit-outer-spin-button,
.time-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.time-input[type=number] {
  -moz-appearance: textfield;
}

.time-text, .pencil-icon {
  line-height: 24px;
}

.btn-timer {
  min-width: 130px;
}

.btn-edit {
  min-width: 70px;
}

.btn-timer.is-mobile {
  min-width: 100px;
}

.btn-edit.is-mobile {
  min-width: 50px;
}

@media (max-width: 1024px) {
  .hot-key-label {
    display: none !important;
  }

  .btn-timer {
    min-width: 100px;
    justify-content: center !important;
  }

  .btn-edit {
    min-width: 50px;
    justify-content: center !important;
  }

  .time-text {
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .pencil-icon {
    margin-left: 0 !important;
  }
}

.timer-toggle {
  transition: 0.15s;
}

.blob {
  transform: scale(1);
  animation: pulse 1.2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.50);
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.7);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }

  100% {
    transform: scale(0.50);
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.7);
  }
}
</style>
